import { SigeProject, Arbeitsschritt, Gefaehrdung, Schutzmassnahme, SigeBlock } from '../types';
import { erzeugeSigePlan, generateSigePlanPdf } from '../../../services/pdf/generatorSigePlan';
import type { SigePlanFusszeile } from '../../../services/pdf/generatorSigePlan';
import { ladePiktogrammBilder } from '../piktogrammBilder';
import { ladeEinweisungQrFuerProjekt } from '../../../services/sigeEinweisungService';
import { projektFuerDruck, sigePlanVorlagenJetzt } from '../../../services/sigePlanVorlagen';
import { sigePlanDateiname, sigePlanFassungRef, sigePlanInhaltsStand, type Blattmass } from '../planAblage';

// PDF-Erzeugung liegt in services/pdf/generatorSigePlan.ts (Muster-Layout).
// Dieser Hook kapselt den Browser-Download und lädt vor dem Download die Piktogramme.

export { generateSigePlanPdf };

/** Was der Download hinterlässt — der Editor legt denselben Stand in der Ablage ab. */
export interface ExportierterPlan {
  blob: Blob;
  /** Der Plan, wie er gedruckt wurde: Breite und Schrift der Firma eingesetzt. */
  druck: SigeProject;
  blatt: Blattmass;
  /**
   * Kennung des Inhaltsstands — aus GENAU den Eingaben, die das Blatt erzeugt
   * haben (Baum, Piktogramme, Einweisungs-QR, Fußzeile). Hier berechnet und
   * nicht im Editor nachgebaut, damit Export und Vorschau dieselbe Kennung
   * für dasselbe Blatt liefern.
   */
  fassungRef: string;
}

export function usePdfExport() {
  const exportPdf = async (
    project: SigeProject,
    arbeitsschritte: Arbeitsschritt[],
    gefaehrdungen: Record<string, Gefaehrdung[]>,
    massnahmen: Record<string, Schutzmassnahme[]>,
    bloecke: SigeBlock[],
    // Projekt-Nr. und -bezeichnung des Hauptprojekts fuer die Fusszeile des Plans.
    fusszeile?: SigePlanFusszeile
  ): Promise<ExportierterPlan> => {
    // Piktogramme und den QR-Code der Online-Einweisung asynchron nachladen,
    // damit beides im exportierten PDF steht. Ohne aktiven Link kommt null —
    // der Plan bleibt dann zeichengleich mit einem Plan ohne Einweisung.
    const [bilder, qr] = await Promise.all([
      ladePiktogrammBilder(bloecke),
      project.project_id
        ? ladeEinweisungQrFuerProjekt(project.project_id, window.location.origin, window.location.pathname)
        : Promise.resolve(null),
    ]);
    // Breite und Schrift, die der Plan nicht selbst traegt, kommen aus den
    // Plotter-Vorlagen der Firma — gelesen beim Klick, damit der Download
    // denselben Stand druckt wie die Vorschau. Nur fuer den Druck.
    const druck = projektFuerDruck(project, sigePlanVorlagenJetzt());
    const plan = erzeugeSigePlan(druck, arbeitsschritte, gefaehrdungen, massnahmen, bloecke, bilder, qr, fusszeile);
    const fassungRef = sigePlanFassungRef(
      project.id,
      sigePlanInhaltsStand({
        druck, arbeitsschritte, gefaehrdungen, massnahmen, bloecke,
        piktoBilder: bilder, einweisungQr: qr, fusszeile,
      }),
    );

    const url = URL.createObjectURL(plan.blob);
    const a = document.createElement('a');
    a.href = url;
    // Derselbe Name wie in der Ablage (planAblage.ts) — Download und
    // abgelegte Fassung sollen als dieselbe Datei erkennbar sein.
    a.download = sigePlanDateiname(project);
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);

    return { blob: plan.blob, druck, blatt: { breiteMm: plan.breiteMm, hoeheMm: plan.hoeheMm }, fassungRef };
  };

  return { exportPdf, generateSigePlanPdf };
}
